import { useState } from 'react';
import { useLocation } from 'react-router-dom';

import endpoints from '../../../../../../api/endpoints';
import { useFetch } from '../../../../../../api/useFetch';
import CommonReports from '../../../../../common/reports';
import { downloadReport } from '../../../../../common/reports/utils/downloadReport';
import { HandleDownloadReport } from '../../../../../common/reports/actions/interfaces';
import { getPermissionToGenerateReports } from '../../../../../common/reports/utils/mapReportToTableStructure';
import { addParams, fetchWithTokenRefresh } from '../../../../../../api/fetchWithTokenRefresh';
import { FiltersFormValues, FiltersInitialData, ReportsData } from '../../../../../common/reports/interfaces';

import locales from './locales';
import FiltersForm from './filters/filtersForm';
import FilterDetails from './filters/filterDetails';
import FiltersSummary from './filters/filtersSummary';
import { mapTableDataIntoApiFormat } from './dataTransformator';
import { filtersValidationSchema } from './filters/filtersValidation';
import { OperatorLookupReportCreateForm, OperatorLookupReportDetailsMetadataResponse } from './interfaces';

const initFilters = {
  name: '',
  statuses: [],
  operationIds: [],
  from: null,
  to: null,
  msisdns: []
};

const Reports = () => {
  const { data = {} as ReportsData, loading, refresh, error } = useFetch<ReportsData>({
    pathname: endpoints.operatorLookupReports
  });

  const handleDeleteReportRequest = (id: string) =>
    fetchWithTokenRefresh(`${endpoints.operatorLookupReports}/${id}`, {
      method: 'delete'
    });
  const handleCancelReportRequest = (id: string) =>
    fetchWithTokenRefresh(`${endpoints.operatorLookupReports}/${id}`, {
      method: 'delete'
    });

  const handleRetryReportRequest = (id: string) =>
    fetchWithTokenRefresh(`${endpoints.operatorLookupReportsRetry(id)}`, {
      method: 'POST'
    });

  const handleRegenerateReportRequest = (id: string) =>
    fetchWithTokenRefresh(`${endpoints.operatorLookupReportsRegenerate(id)}`, {
      method: 'POST'
    });

  const handleGenerateReportRequest = (values: OperatorLookupReportCreateForm) =>
    fetchWithTokenRefresh(endpoints.operatorLookupReports, addParams('POST', mapTableDataIntoApiFormat(values)));

  const handleDownloadReport: HandleDownloadReport = (id, name, size) => {
    downloadReport(id, `${name}.csv`, 'operatorLookup', size);
  };

  const location = useLocation<{ filters: FiltersInitialData } | undefined>();

  const [initialValues, setInitialValues] = useState(location.state?.filters || initFilters);

  return (
    <CommonReports
      error={error}
      filters={{
        form: (filtersFormValues: FiltersFormValues<OperatorLookupReportCreateForm>) => (
          <FiltersForm {...filtersFormValues} />
        ),
        validation: filtersValidationSchema,
        details: (metadata: OperatorLookupReportDetailsMetadataResponse) => <FilterDetails filters={metadata} />,
        summary: (metadata: OperatorLookupReportDetailsMetadataResponse) => <FiltersSummary filters={metadata} />,
        initialValues,
        clearValues: () => {
          setInitialValues(initFilters);
        },
        predefinedReportGeneration: Boolean(location.state?.filters)
      }}
      locales={locales}
      data={data}
      emptyMsg="You don't have any Operator Lookup reports to display"
      loading={loading}
      refresh={refresh}
      handleDeleteReportRequest={handleDeleteReportRequest}
      handleCancelReportRequest={handleCancelReportRequest}
      handleRetryReportRequest={handleRetryReportRequest}
      handleRegenerateReportRequest={handleRegenerateReportRequest}
      handleGenerateReportRequest={handleGenerateReportRequest}
      handleDownloadReport={handleDownloadReport}
      generationAllowed={getPermissionToGenerateReports(data.limits?.status)}
    />
  );
};

export default Reports;
